import { t } from '@lingui/core/macro';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import { isValidUKPhoneNumber } from 'utils/phoneNumberHelper';
import * as yup from 'yup';
import {
  getTotalDebt,
  overRequestedAmountMessage,
} from '../RefinancingDebts/refinancing.helpers';
import { refinancingSchema } from '../RefinancingDebts/refinancing.schema';
import { TSignupFormData } from './SignupForm.types';
import { dayRegex, isOver18, monthRegex } from './SignUpFormHelper';

export const DaySchema = yup
  .string()
  .required('Day required')
  .matches(dayRegex, 'Enter a valid day');

export const MonthSchema = yup
  .string()
  .required('Month required')
  .matches(monthRegex, 'Enter a valid month');

export const YearSchema = yup
  .string()
  .min(4, 'Year required')
  .max(4, 'Invalid year')
  .required('Year required')
  .test(
    'Is 18 or over',
    'You must be 18 years or older',
    function (value, testContext) {
      if (
        !Number(testContext.parent.dobMonth) ||
        !Number(testContext.parent.dobDay)
      ) {
        // Don't check age unless day + month entered
        return true;
      }
      return isOver18(
        value,
        testContext.parent.dobMonth,
        testContext.parent.dobDay,
      );
    },
  );

/**
 * Pulled out shared fields used in the reintroduction and non-reintroduction Schemas to avoid duplicating the values.
 * Schemas are different because reintroduction fields need to be optional.
 */
const addressSchemaSharedFields = {
  house_name: yup.string(),
  house_number: yup.string(),
  street_line_2: yup.string(),
  residential_status: yup.string(),
  equifax_token: yup.string(),
};

const AddressSchema = yup.object().shape({
  ...addressSchemaSharedFields,
  street_line_1: yup.string().required('Street line 1 is required'),
  town: yup.string().required('Town is required'),
  postcode: yup.string().required('Postcode is required'),
});

export const PeopleSchema = yup.object().shape({
  firstName: yup.string().required('First name required'),
  lastName: yup.string().required('Last name required'),
  dobDay: DaySchema,
  dobMonth: MonthSchema,
  dobYear: YearSchema,
  phoneNumber: yup
    .string()
    .required('Phone number required')
    .test(
      'Is a valid number',
      'Please use a valid phone number format',
      isValidUKPhoneNumber,
    ),
  secondaryPhoneNumber: yup
    .string()
    .test(
      'Is a valid number',
      'Please use a valid phone number format',
      isValidUKPhoneNumber,
    ),
  email: yup
    .string()
    .email('Please use a valid email format')
    .required('Email address is required'),
  residentialAddresses: yup.array().of(AddressSchema).ensure().required(),
  roles: yup
    .array()
    .of(yup.string())
    .ensure()
    .min(1, 'Please select at least one option')
    .required(),
  isShareholder: yup
    .string()
    .oneOf(['true', 'false'], 'Shareholder status is required'),
  privacyPolicyAgreement: yup
    .boolean()
    .required('Privacy policy is required')
    .oneOf([true], 'The terms and conditions must be accepted.'),
});

const PeopleSchemaReintroduction = yup.object().shape({
  firstName: yup.string(),
  lastName: yup.string(),
  dobDay: yup.string(),
  dobMonth: yup.string(),
  dobYear: yup.string(),
  phoneNumber: yup.string(),
  secondaryPhoneNumber: yup.string(),
  email: yup.string(),
  residentialAddresses: yup.array(),
  roles: yup.array(),
  isShareholder: yup.string(),
  privacyPolicyAgreement: yup.boolean(),
});

export const minimumDataRequirementsSchema = (isReintroduction = false) =>
  yup.object().shape({
    companyName: yup.string().required('Company name is required'),
    companyNumber: yup.string().required('Company number is required'),
    companyType: yup.string().required('Company type is required'),
    startDateMonth: yup.string().required('Start trading month required'),
    startDateYear: yup.string().required('Start trading year required'),
    last12MonthsTurnover: yup.string().required('Turnover required'),
    amount: yup
      .string()
      .required('Please enter a requested amount')
      .test('minCurrencyAmount', 'Between £1,000 and £1,000,000', (value) => {
        const numericValue = convertCurrencyToNumber(value);
        if (!numericValue) return false;

        return numericValue >= 1000;
      })
      .test('maxCurrencyAmount', 'Any amount up to £1,000,000', (value) => {
        const numericValue = convertCurrencyToNumber(value);
        if (!numericValue) return false;
        return numericValue <= 1000000;
      }),
    loanDuration: yup
      .string()
      .oneOf(
        ['12-months', '24-months', '36-months', '48-months', '60-months'],
        'Please choose a term length',
      )
      .required('Please choose a term length'),
    loanPurpose: yup.string().required(() => t`Please choose a reason`),
    applicant: isReintroduction ? PeopleSchemaReintroduction : PeopleSchema,
    guarantors: yup
      .array()
      .of(isReintroduction ? PeopleSchemaReintroduction : PeopleSchema)
      .ensure(),
  });

export const schema = (isReintroduction = false, showRefinancing = false) => {
  const baseSchema = minimumDataRequirementsSchema(isReintroduction).shape({
    isVatRegistered: yup
      .string()
      .nullable()
      .oneOf(['true', 'false', null])
      .notRequired(),
    tradingName: yup.string(),
    website: yup
      .string()
      .matches(
        /^$|((http|https):\/\/)?[a-zA-Z0-9./?:@\-_=#]+\.([a-zA-Z0-9&./?:@\-_=#])*/,
        'Please enter a valid website',
      ),
    ...(showRefinancing ? { refinancing: refinancingSchema } : {}),
  });

  if (!showRefinancing) return baseSchema;

  // Block submission when debts exceed the requested amount.
  return baseSchema.test('requestedAmountCoversDebt', function (values) {
    const { amount, refinancing } = (values ?? {}) as TSignupFormData;
    const requestedAmount = convertCurrencyToNumber(amount) || 0;
    // Skip until an amount is entered so we don't stack on the "required" error.
    if (!requestedAmount) return true;

    const totalDebt = getTotalDebt(refinancing?.debts);
    if (totalDebt <= requestedAmount) return true;

    return this.createError({
      path: 'amount',
      message: overRequestedAmountMessage(totalDebt),
    });
  });
};
